/**
 * Bill status display helpers.
 *
 * A bill now carries a payables[] child schema — one payable per due cycle,
 * each settled by at most one payment — and the server rolls those up onto the
 * bill (payables_count / paid_count / overdue_count / next_due_date). When that
 * rollup is present it is the truth, so getPaymentStatus reads it first.
 *
 * The old 10-day heuristic (a bill counted as PAID if any payment landed in the
 * 10 days before its due date) survives ONLY as the fallback for bills that have
 * no payables yet — legacy bills whose payables have never been seeded.
 */

// One place for the chip colours, shared with the payables modal so a "paid"
// chip is the same green wherever it is drawn.
export const STATUS_COLORS = {
    closed: '#757575',   // Gray - inactive/completed
    paid: '#4CAF50',     // Green
    overdue: '#F44336',  // Red
    upcoming: '#9E9E9E', // Neutral gray - due further out than DUE_SOON_DAYS
    unpaid: '#FF9800',   // Orange - legacy heuristic only
    none: '#9E9E9E',     // Gray
};

// How near its due date an unpaid cycle has to be for the chip to turn red.
// Inside this many days the owner has to act on it now, so the chip is drawn
// in the OVERDUE red instead of the neutral upcoming gray -- 0 days (due
// today) is inside the window and the most urgent of all. Only the colour
// changes; the label still names the due date exactly as before. Kept in step
// with mobile/src/utils/billStatusHelper.js, which uses the same threshold.
export const DUE_SOON_DAYS = 5;

const MS_PER_DAY = 86400000;

// 'YYYY-MM-DD' (optionally with a time part) -> local-midnight Date, or null
// when missing/unparseable. Never new Date(str), which parses a bare date as
// UTC and lands on the wrong day in UTC+8.
const parseLocalDate = (value) => {
    if (typeof value !== 'string') return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!match) return null;
    const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const date = new Date(y, m - 1, d);
    // Reject rollovers like 2026-02-31.
    if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
    return date;
};

// Whole local calendar days from today to `dueDate` (negative once it has
// passed), rounded so a DST shift of an hour cannot flip the count.
const daysUntil = (dueDate, today) => {
    const due = parseLocalDate(dueDate);
    if (!due) return null;
    const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return Math.round((due - midnight) / MS_PER_DAY);
};

// Red once an unpaid cycle is DUE_SOON_DAYS days away or closer (or past),
// gray while it is further out or carries no date at all.
const upcomingColor = (dueDate, today) => {
    const days = daysUntil(dueDate, today);
    return days !== null && days <= DUE_SOON_DAYS ? STATUS_COLORS.overdue : STATUS_COLORS.upcoming;
};

const toCount = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const pluralDays = (n) => `${n} day${n === 1 ? '' : 's'}`;

/**
 * Status of a single payable (one due cycle).
 * Trusts the server-computed `status` and only derives one when it is missing.
 * @param {Object} payable
 * @returns {Object} { status, label, color }
 */
export const getPayableStatus = (payable) => {
    if (!payable) return { status: 'upcoming', label: 'UPCOMING', color: STATUS_COLORS.upcoming };

    let status = payable.status;
    if (!status) {
        if (payable.payment_id) {
            status = 'paid';
        } else if (payable.due_date && payable.due_date < new Date().toISOString().slice(0, 10)) {
            status = 'overdue';
        } else {
            status = 'upcoming';
        }
    }

    return {
        status,
        label: String(status).toUpperCase(),
        color: STATUS_COLORS[status] || STATUS_COLORS.none,
    };
};

/**
 * "3 days early" / "5 days late" / "on time" for a settled payable, and an
 * em dash for one that is still unpaid.
 * @param {Object} payable
 * @returns {Object} { text, color }
 */
export const earlyLateLabel = (payable) => {
    const none = { text: '—', color: STATUS_COLORS.none };
    if (!payable) return none;

    const settled = Boolean(payable.payment_id || payable.paid_on);
    const raw = payable.days_diff;
    if (!settled || raw === null || raw === undefined || raw === '') return none;

    const diff = Number(raw);
    if (!Number.isFinite(diff)) return none;

    if (diff > 0) return { text: `${pluralDays(diff)} late`, color: STATUS_COLORS.overdue };
    if (diff < 0) return { text: `${pluralDays(-diff)} early`, color: STATUS_COLORS.paid };
    return { text: 'on time', color: STATUS_COLORS.paid };
};

/**
 * Utility function to determine the payment status of a bill
 * @param {Object} bill - The bill object containing the payables rollup (or, for
 *                        legacy bills, due_date and a payments array) and status
 * @param {Date} [today=new Date()] - "Now", injectable for tests. Only the
 *                        due-soon colour depends on it.
 * @returns {Object} Status object with status, label, color, source and
 *                   nextDueDate properties
 */
export const getPaymentStatus = (bill, today = new Date()) => {
    if (!bill) {
        return {
            status: 'no_payments',
            label: 'NO PAYMENTS',
            color: STATUS_COLORS.none,
            source: 'legacy',
            nextDueDate: null,
        };
    }

    // Closed bills are always displayed as closed (excluded from reports)
    if (bill.status === 'closed') {
        return {
            status: 'closed',
            label: 'CLOSED',
            color: STATUS_COLORS.closed,
            source: 'legacy',
            nextDueDate: null,
        };
    }

    // --- Rollup-first: the bill knows its own cycles ---
    const payablesCount = toCount(bill.payables_count);
    if (payablesCount > 0) {
        const nextDueDate = bill.next_due_date || null;

        // Any missed cycle outranks everything else — that is the number the
        // user needs to see on the list.
        if (toCount(bill.overdue_count) > 0) {
            return {
                status: 'overdue',
                label: 'OVERDUE',
                color: STATUS_COLORS.overdue,
                source: 'payables',
                nextDueDate,
            };
        }

        const paidCount = toCount(bill.paid_count);

        if (paidCount >= payablesCount) {
            return {
                status: 'paid',
                label: 'PAID',
                color: STATUS_COLORS.paid,
                source: 'payables',
                nextDueDate: null,
            };
        }

        // Nothing is overdue and at least one cycle is settled, so nothing is
        // owed right now — the cycles still open are all in the future. The
        // badge describes what is owed NOW, so that reads PAID, with the next
        // due date carried in the label rather than downgrading it to UPCOMING
        // (which is what made a monthly bill with future cycles never read PAID).
        if (paidCount > 0) {
            return {
                status: 'paid',
                label: nextDueDate ? `PAID · NEXT ${nextDueDate}` : 'PAID',
                color: STATUS_COLORS.paid,
                source: 'payables',
                nextDueDate,
            };
        }

        // Nothing paid and nothing overdue — the first cycle is simply still
        // due. The label names the date; the colour says how urgent it is.
        return {
            status: 'upcoming',
            label: nextDueDate ? `DUE ${nextDueDate}` : 'UPCOMING',
            color: upcomingColor(nextDueDate, today),
            source: 'payables',
            nextDueDate,
        };
    }

    // --- Fallback: legacy bills with no payables, judged by the 10-day window ---
    const payments = bill.payments || [];
    const dueDate = new Date(bill.due_date);

    // No payments recorded
    if (payments.length === 0) {
        return {
            status: 'no_payments',
            label: 'NO PAYMENTS',
            color: STATUS_COLORS.none,
            source: 'legacy',
            nextDueDate: bill.due_date || null,
        };
    }

    // Calculate the earliest acceptable payment date (10 days before due date)
    const earliestPaymentDate = new Date(dueDate);
    earliestPaymentDate.setDate(earliestPaymentDate.getDate() - 10);

    // Check if any payment was made within 10 days before (or on) the due date
    const hasPaidPayment = payments.some(payment => {
        const paymentDate = new Date(payment.payment_date);
        return paymentDate >= earliestPaymentDate && paymentDate <= dueDate;
    });

    if (hasPaidPayment) {
        return {
            status: 'paid',
            label: 'PAID',
            color: STATUS_COLORS.paid,
            source: 'legacy',
            nextDueDate: bill.due_date || null,
        };
    }

    // Has payments but none are within the valid window
    return {
        status: 'unpaid',
        label: 'UNPAID',
        color: STATUS_COLORS.unpaid,
        source: 'legacy',
        nextDueDate: bill.due_date || null,
    };
};
